import React, { useState, useEffect } from 'react';
import { securityApi } from '../api/client';

const styles: Record<string, React.CSSProperties> = {
  page: { fontFamily: 'Inter, system-ui, sans-serif' },
  summaryCard: { background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 20, marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  disclaimer: { background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '12px 16px', fontSize: 12, color: '#92400e', marginBottom: 20, lineHeight: 1.6 },
  card: { background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 20, marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  sectionTitle: { fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 12, marginTop: 20 },
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: 12 },
  th: { textAlign: 'left' as const, padding: '10px 12px', borderBottom: '2px solid #e2e8f0', color: '#475569', fontWeight: 600, fontSize: 11, textTransform: 'uppercase' as const, letterSpacing: 0.4 },
  td: { padding: '10px 12px', borderBottom: '1px solid #f1f5f9', color: '#1e293b', verticalAlign: 'top' as const },
  badge: { display: 'inline-block', padding: '2px 8px', borderRadius: 9999, fontSize: 10, fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: 0.3 },
  barContainer: { display: 'flex', gap: 2, height: 24, borderRadius: 8, overflow: 'hidden', marginTop: 10, marginBottom: 8 },
  legendItem: { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#475569', marginRight: 12 },
  legendDot: { width: 10, height: 10, borderRadius: '50%' },
  btn: { padding: '5px 10px', background: '#4f46e5', color: 'white', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer' },
  btnSmall: { padding: '4px 8px', background: '#f1f5f9', color: '#374151', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 11, cursor: 'pointer' },
  select: { padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, boxSizing: 'border-box' as const },
  loading: { textAlign: 'center' as const, padding: 40, color: '#64748b', fontSize: 13 },
  error: { textAlign: 'center' as const, padding: 40, color: '#dc2626', fontSize: 13 },
  modal: { position: 'fixed' as const, inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 },
  modalContent: { background: 'white', borderRadius: 12, padding: 24, width: 400, maxWidth: '90vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' },
};

const statusConfig: Record<string, { color: string; bg: string; label: string }> = {
  not_started: { color: '#475569', bg: '#f1f5f9', label: 'Not Started' },
  planned: { color: '#854d0e', bg: '#fef9c3', label: 'Planned' },
  implemented: { color: '#1e40af', bg: '#dbeafe', label: 'Implemented' },
  configured: { color: '#3730a3', bg: '#e0e7ff', label: 'Configured' },
  tested: { color: '#166534', bg: '#dcfce7', label: 'Tested' },
  independently_assessed: { color: '#065f46', bg: '#d1fae5', label: 'Independently Assessed' },
  requires_dhs_integration: { color: '#9a3412', bg: '#ffedd5', label: 'Requires DHS Integration' },
  requires_government_authorization: { color: '#6b21a8', bg: '#f3e8ff', label: 'Requires Gov Authorization' },
};

const statusOptions = Object.keys(statusConfig);

export default function SecurityReadiness() {
  const [controls, setControls] = useState<any[]>([]);
  const [summary, setSummary] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingControl, setEditingControl] = useState<any>(null);
  const [newStatus, setNewStatus] = useState('');
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [ctrlRes, sumRes] = await Promise.all([
        securityApi.controls(),
        securityApi.summary(),
      ]);
      const ctrlList = Array.isArray(ctrlRes) ? ctrlRes : ctrlRes?.items || ctrlRes?.controls || [];
      setControls(ctrlList);
      setSummary(sumRes?.status_counts || sumRes?.summary || {});
    } catch (err: any) {
      setError(err.message || 'Failed to load security readiness data');
    }
    setLoading(false);
  };

  const handleUpdateStatus = async () => {
    if (!editingControl || !newStatus) return;
    setUpdating(true);
    try {
      await securityApi.updateControl({
        control_id: editingControl.id || editingControl.control_id,
        status: newStatus,
      });
      setEditingControl(null);
      setNewStatus('');
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to update control status');
    }
    setUpdating(false);
  };

  if (loading) return <div style={styles.loading}>Loading security readiness data...</div>;
  if (error && controls.length === 0) return <div style={styles.error}>{error}</div>;

  // Group controls by category
  const grouped: Record<string, any[]> = {};
  controls.forEach((ctrl: any) => {
    const cat = ctrl.category || 'Other';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(ctrl);
  });

  // Summary bar chart data
  const totalControls = Object.values(summary).reduce((a: number, b: number) => a + b, 0) || controls.length || 1;

  return (
    <div style={styles.page}>
      {/* Summary Card */}
      <div style={styles.summaryCard}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 10 }}>Security Control Status Summary</div>

        {/* Bar Chart */}
        <div style={styles.barContainer}>
          {statusOptions.map(status => {
            const count = summary[status] || 0;
            if (count === 0) return null;
            const pct = (count / totalControls) * 100;
            const cfg = statusConfig[status];
            return (
              <div
                key={status}
                title={`${cfg.label}: ${count}`}
                style={{
                  width: `${pct}%`,
                  background: cfg.bg,
                  border: `1px solid ${cfg.color}20`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: count > 0 ? 20 : 0,
                }}
              >
                <span style={{ fontSize: 9, fontWeight: 700, color: cfg.color }}>{count}</span>
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>
          {statusOptions.map(status => {
            const count = summary[status] || 0;
            if (count === 0) return null;
            const cfg = statusConfig[status];
            return (
              <span key={status} style={styles.legendItem}>
                <span style={{ ...styles.legendDot, background: cfg.bg, border: `1px solid ${cfg.color}` }} />
                {cfg.label} ({count})
              </span>
            );
          })}
        </div>
      </div>

      {/* Disclaimer */}
      <div style={styles.disclaimer}>
        <strong>Disclaimer:</strong> Security readiness is self-assessed. Status does not imply independent verification or government authorization.
        This dashboard tracks target readiness posture for future compliance activities.
      </div>

      {/* Controls by Category */}
      {Object.entries(grouped).map(([category, categoryControls]) => (
        <div key={category}>
          <div style={styles.sectionTitle}>{category}</div>
          <div style={{ ...styles.card, overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Control</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Evidence</th>
                  <th style={styles.th}>Last Assessed</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {categoryControls.map((ctrl: any, i: number) => {
                  const cfg = statusConfig[ctrl.status] || statusConfig.not_started;
                  return (
                    <tr key={ctrl.id || ctrl.control_id || i}>
                      <td style={{ ...styles.td, fontWeight: 500 }}>{ctrl.name || ctrl.control_name}</td>
                      <td style={styles.td}>
                        <span style={{ ...styles.badge, background: cfg.bg, color: cfg.color }}>
                          {cfg.label}
                        </span>
                      </td>
                      <td style={{ ...styles.td, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {ctrl.evidence || '-'}
                      </td>
                      <td style={styles.td}>
                        {ctrl.last_assessed ? new Date(ctrl.last_assessed).toLocaleDateString() : '-'}
                      </td>
                      <td style={styles.td}>
                        <button
                          style={styles.btn}
                          onClick={() => { setEditingControl(ctrl); setNewStatus(ctrl.status || 'not_started'); }}
                          aria-label={`Update status for ${ctrl.name || ctrl.control_name}`}
                        >
                          Update Status
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {/* If no grouped controls, show empty state */}
      {Object.keys(grouped).length === 0 && (
        <div style={styles.loading}>No security controls configured yet.</div>
      )}

      {/* Update Status Modal */}
      {editingControl && (
        <div style={styles.modal} onClick={() => setEditingControl(null)}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()} role="dialog" aria-label="Update control status">
            <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 14 }}>
              Update Status: {editingControl.name || editingControl.control_name}
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }} htmlFor="status-select">
                New Status
              </label>
              <select
                id="status-select"
                style={{ ...styles.select, width: '100%' }}
                value={newStatus}
                onChange={e => setNewStatus(e.target.value)}
              >
                {statusOptions.map(s => (
                  <option key={s} value={s}>{statusConfig[s].label}</option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button style={styles.btnSmall} onClick={() => setEditingControl(null)}>Cancel</button>
              <button
                style={{ ...styles.btn, opacity: updating ? 0.5 : 1 }}
                onClick={handleUpdateStatus}
                disabled={updating}
              >
                {updating ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
